import { Component, computed, inject, input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxAmount } from '@onyx/angular';
import { OnyxAmountPipe, OnyxWidgetBoxComponent } from '@onyx/angular';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { OrderStatus } from '../../../common/enums/order-status';
import { OrderHelper } from '../../../common/helpers/order.helper';
import type { OrderPayment } from '../../../common/interfaces/order';

@Component({
  selector: 'app-order-modal-outstanding-payment',
  imports: [OnyxWidgetBoxComponent],
  templateUrl: './order-modal-outstanding-payment.component.html',
  styleUrl: './order-modal-outstanding-payment.component.scss',
})
export class OrderModalOutstandingPaymentComponent {
  private readonly amountPipe = inject(OnyxAmountPipe);
  private readonly translateService = inject(TranslateService);

  public readonly payment = input.required<OrderPayment>();

  protected readonly I18N = 'orders.orderModal';

  protected net = computed(() => {
    const paymentDetails = OrderHelper.getPaymentDetails(this.payment());
    if (!paymentDetails) return null;
    if (!this.payment().history.length) return paymentDetails.invoiceNetAmount;

    return {
      ...paymentDetails.invoiceNetAmount,
      value: paymentDetails.remainingNet,
    };
  });

  protected vat = computed(() => {
    const paymentDetails = OrderHelper.getPaymentDetails(this.payment());
    if (!paymentDetails) return null;
    if (!this.payment().history.length) return paymentDetails.invoiceVatAmount;

    return {
      ...paymentDetails.invoiceVatAmount,
      value: paymentDetails.remainingVat,
    };
  });

  protected remainingToPay = computed(() => {
    const vat = this.vat();
    const net = this.net();
    if (!vat || !net) return null;

    const vatValue = vat.value;
    const netValue = net.value;

    if (netValue === 0 && vatValue === 0) {
      return this.translateService.instant(
        `${DictionaryCode.ORDER_STATUS}.${OrderStatus.PAID}`,
      );
    } else if (netValue === 0) {
      return this.amountPipe.transform(vat, 'value', false);
    } else if (vatValue === 0) {
      return this.amountPipe.transform(net, 'value', false);
    } else if (vat.currency === net.currency) {
      const total: OnyxAmount = {
        ...net,
        value: netValue + vatValue,
      };

      return this.amountPipe.transform(total, 'value', false);
    }

    return `${this.amountPipe.transform(net, 'value', false)} + ${this.amountPipe.transform(vat, 'value', false)}`;
  });
}
